import React, {useState, useEffect, useImperativeHandle} from 'react'
import {Table, Button, Spin, Empty} from 'antd'
import {Search} from './components/Search'
import {LoadingOutlined} from '@ant-design/icons'
import {connect} from 'react-redux'
import {getPagination, deleteByIds} from 'redux/actions/repositoryActions'
import {DataTableRequest, DataTableColumns, DataTableOrders} from '../core/_dt'
import type {TableProps} from 'antd/es/table'
import type {ExpandableConfig, TableRowSelection} from 'antd/es/table/interface'

const loadingIcon = <LoadingOutlined style={{fontSize: 40}} spin />

type Props = {
  props?: any
  columns: any
  defaultOrder?: DataTableOrders[]
  url: string
  enableRowSelection?: boolean
  selectedKeys?: string[]
  rowSelectionActions?: React.ReactNode
  selectionType?: 'checkbox' | 'radio'
  tableSize?: 'small' | 'middle' | 'large'
  tableLayout?: 'auto' | 'fixed'
  tablePaginationPosition?: 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight'
  searchRegex?: boolean
  searchFull?: boolean
  globalSearch?: boolean
  bordered?: boolean
  showHeader?: boolean
  showTotal?: boolean
  showSizeChanger?: boolean
  panelHeader?: React.ReactNode
  loading?: any
  getPagination: Function
  deleteByIds: Function
  dataTablesResponse?: any
  expandable?: ExpandableConfig<any> | undefined
  isTable?: boolean
  itemPerPage?: number
  autoTrigger?: boolean
}

const dt = React.forwardRef<any, Props>((props, ref) => {
  const {
    url,
    columns,
    defaultOrder,
    enableRowSelection = false,
    rowSelectionActions,
    selectionType = 'checkbox',
    globalSearch = true,
    panelHeader,
    bordered = true,
    showTotal = false,
    showHeader = true,
    expandable,
    showSizeChanger = true,
    tableSize = 'middle',
    tableLayout = 'auto',
    tablePaginationPosition = 'bottomRight',
    searchRegex = false,
    // searchFull = false,
    isTable = false,
    autoTrigger = true,
    itemPerPage = 10,
  } = props

  const [dtRequest, setDtRequest] = useState<DataTableRequest>({
    start: 0,
    index: 0,
    draw: 0,
    page: 1,
    length: itemPerPage,
    filters: [],
    // columns: invokeColumns(columns),
    columns: [],
    orders: [],
    search: {
      searchRegex: searchRegex ?? false,
      searchValue: null,
    },
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(props.selectedKeys ?? [])
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [init, setInit] = useState<boolean>(autoTrigger)

  useEffect(() => {
    if (autoTrigger || init) {
      fetch()
    } else {
      setInit(true)
    }

    // eslint-disable-next-line
  }, [dtRequest])

  useImperativeHandle(ref, () => ({
    async fetch() {
      setDtRequest({
        ...dtRequest,
        draw: dtRequest.draw + 1,
        page: 1,
        orders: [],
        search: {
          searchRegex: searchRegex ?? false,
          searchValue: null,
        },
      })
    },
    async getSelectedKeys() {
      return selectedRowKeys
    },
    async filterByColumn() {
      setDtRequest({
        ...dtRequest,
        draw: dtRequest.draw + 1,
        page: 1,
        orders: [],
        columns: invokeColumns(columns),
        search: {
          searchRegex: searchRegex ?? false,
          searchValue: null,
        },
      })
    },
  }))

  const onSearchChange = (e: null | string) => {
    setDtRequest({
      ...dtRequest,
      draw: dtRequest.draw + 1,
      page: 1,
      search: {
        searchRegex: searchRegex ?? false,
        searchValue: e,
      },
    })
  }

  function invokeColumns(columns: []) {
    let dtColumns: DataTableColumns[] = []
    if (columns) {
      columns.forEach((r: any) => {
        if (Object.keys(r).length > 0 && (r.data || r.name)) {
          dtColumns.push({
            name: r.name,
            data: r.data,
            orderable: r.orderable ?? r.orderable ?? false,
            searchable: r.searchable ?? false,
            searchRegex: r.searchRegex ?? false,
            searchValue: r.searchValue ?? null,
          })
        }
      })
    }
    return dtColumns
  }

  function fetch() {
    let payload
    if (dtRequest?.draw === 0) {
      payload = {
        ...dtRequest,
        orders: defaultOrder,
        columns: invokeColumns(columns),
      }
    } else {
      payload = {
        ...dtRequest,
        columns: invokeColumns(columns),
      }
    }

    props
      .getPagination({
        key: url,
        url: url,
        isTable: isTable,
        payload: payload,
      })
      .then((response: any) => {})
      .catch((response: any) => {})
  }

  const deleteRows = async () => {
    setDeleteLoading(true)
    await props
      .deleteByIds({
        key: url,
        url: url,
        payload: selectedRowKeys,
      })
      .then((response: any) => {
        setDeleteLoading(false)
      })
      .catch((response: any) => {
        setDeleteLoading(false)
      })
  }

  const onTableChange: TableProps<any>['onChange'] = async (pagination, _, sorter, extra) => {
    switch (extra.action) {
      case 'paginate':
        setDtRequest({
          ...dtRequest,
          draw: dtRequest.draw + 1,
          page: pagination.current ?? 1,
          length: pagination.pageSize ?? 10,
        })
        break
      case 'sort':
        let _sorter: DataTableOrders[] = []
        if (Array.isArray(sorter)) {
          sorter.forEach((s, i) => {
            _sorter.push({
              columnName: `${s.field}`,
              direction: s.order === 'ascend' ? 'asc' : 'desc',
            })
          })
        } else {
          if (sorter.order !== undefined)
            _sorter.push({
              columnName: `${sorter.field}`,
              direction: sorter.order === 'ascend' ? 'asc' : 'desc',
            })
        }

        await setDtRequest({
          ...dtRequest,
          draw: dtRequest.draw + 1,
          orders: _sorter,
        })
        break
      case 'filter':
        break
      default:
        break
    }
  }

  const handleRowSelection = () => {
    if (!enableRowSelection) return {}
    return {
      rowSelection: {
        type: selectionType,
        ...rowSelection,
      },
    }
  }

  function renderPanelHeader() {
    if (panelHeader === undefined) return null

    return panelHeader
  }

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    defaultSelectedRowKeys: props.selectedKeys,
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Anything', // Column configuration not to be checked
      name: record.name,
    }),
    preserveSelectedRowKeys: true,
    onSelect: (record: any, info) => {
      if (info) {
        setSelectedRowKeys([...selectedRowKeys, record.key ?? record.id])
      } else {
        const newSelectedRows = selectedRowKeys.filter((r) => r !== record.id)
        setSelectedRowKeys(newSelectedRows)
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        setSelectedRowKeys([...selectedRowKeys, ...changeRows.map((record) => record['key'])])
      } else {
        setSelectedRowKeys([])
      }
    },
  }

  const isDtValid = () => {
    return props.dataTablesResponse[url]?.error === null || props.dataTablesResponse[url]?.error === undefined
  }

  const renderBodyError = () => {
    if (isDtValid()) return {components: {}}
    return {
      components: {
        body: {
          row: () => (
            <tr>
              <td colSpan={columns.length}>
                <div className='text-center'>
                  <p className='mb-0 fs-6'>{props.dataTablesResponse[url]?.error ?? 'An error caught with no details'}</p>
                </div>
              </td>
            </tr>
          ),
        },
      },
    }
  }

  const getTotalPage = (): number => {
    if (props.dataTablesResponse[url]) {
      if (props.dataTablesResponse[url]?.recordsFiltered < props.dataTablesResponse[url]?.recordsTotal) {
        return props.dataTablesResponse[url]?.recordsFiltered
      } else {
        return props.dataTablesResponse[url]?.recordsTotal
      }
    }
    return 0
  }

  return (
    <>
      <div className='row mb-3'>
        <div className='col-xxl-6 col-xl-8 col-lg-7 col-md-7 col-sm-12'>
          {globalSearch && <Search callback={(e: null | string) => onSearchChange(e)} loading={props.loading[url] ?? false} disabled={!isDtValid()} />}
          {/* <div className={`${enableRowSelection || panelHeader ? "col-sm-6 col-lg-auto" : (searchFull ? "col-6" : "col-sm-6 col-md-auto")}`}>
                        {globalSearch && (
                            <Search callback={(e: null | string) => onSearchChange(e)} loading={props.loading[url] ?? false} disabled={!isDtValid()} />
                        )}
                    </div> */}
        </div>
        <div className='col-xxl-9 col-xl-8 col-lg-7 col-md-4 col-sm-4 d-flex mb-1 d-none'>
          {enableRowSelection &&
            (rowSelectionActions ? (
              rowSelectionActions
            ) : (
              <>
                <Button
                  type='primary'
                  className={`rounded-0 ${props.loading[url]?.delete ?? false ? 'px-4' : 'px-7'} fw-bold me-2`}
                  disabled={(props.loading[url] ?? false) || selectedRowKeys.length === 0}
                  loading={deleteLoading}
                  onClick={deleteRows}
                  danger
                >
                  Delete
                </Button>
              </>
            ))}
          {renderPanelHeader()}
        </div>
      </div>
      {/* <div className={`row g-2 ${enableRowSelection || panelHeader ? ((searchFull ? "w-100" : "float-end")) : (searchFull ? "w-100" : "float-end")} mb-3`} key={"search-component"}>
                {renderPanelHeader()}
                {enableRowSelection && (
                    <div className="col-auto">
                        <Button type="primary"
                            className={`rounded-0 ${(props.loading[url]?.delete ?? false) ? "px-4" : "px-7"} fw-bold`}
                            disabled={(props.loading[url] ?? false) || selectedRowKeys.length === 0}
                            loading={deleteLoading}
                            onClick={deleteRows}
                            danger>
                            Delete
                        </Button>
                    </div>
                )}
                <div className={`${enableRowSelection || panelHeader ? "col-sm-12 col-lg-auto" : (searchFull ? "col-12" : "col-sm-12 col-md-auto")}`}>
                    {globalSearch && (
                        <Search callback={(e: null | string) => onSearchChange(e)} loading={props.loading[url] ?? false} disabled={!isDtValid()} />
                    )}
                </div>
            </div> */}
      <Table
        className='rounded-0'
        showHeader={showHeader}
        {...handleRowSelection()}
        tableLayout={tableLayout}
        size={tableSize}
        pagination={{
          total: getTotalPage(),
          showSizeChanger: showSizeChanger,
          current: dtRequest?.page ?? 1,
          pageSize: dtRequest?.length ?? 10,
          showTotal: (total: any, range: any) => (showTotal ? `${range[0]} - ${range[1]} of ${total} items` : <></>),
          position: [tablePaginationPosition],
        }}
        bordered={bordered}
        rowKey={'id'}
        columns={columns}
        loading={{indicator: <Spin indicator={loadingIcon} tip={<p className='mt-14 fs-4'>Loading...</p>} className='mt-n10' />, spinning: props.loading[url] ?? false}}
        dataSource={props.dataTablesResponse[url]?.data ?? []}
        {...renderBodyError()}
        onChange={onTableChange}
        scroll={{x: 'max-content'}}
        expandable={expandable}
        locale={{
          emptyText: <Empty children={<p className='fs- fw-bolder'>TIDAK ADA DATA DITEMUKAN</p>} description={null} />,
        }}
      />
    </>
  )
})

const reduxState = (state: any) => ({
  loading: state.repository.loading,
  dataTablesResponse: state.repository.dataTablesResponse,
  getPagination: state.getPagination,
  deleteByIds: state.deleteByIds,
})

const mapsToProps = (dispatch: any) => ({
  getPagination: (data: DataTableRequest) => dispatch(getPagination(data)),
  deleteByIds: (data: any) => dispatch(deleteByIds(data)),
})

export default connect(reduxState, mapsToProps, null, {forwardRef: true})(dt)
